/**
 * Which certificates one Audit Process scope will issue.
 *
 * Integrated and combined audits deliberately run Application Review, scheduling, the audit and
 * Technical Review as ONE grouped scope, for audit efficiency. Certificate issuance is where that
 * grouping has to end: each member standard gets its own certificate number, its own single
 * standard name, and its own row in Certificate Monitoring. A single-standard scope resolves to
 * exactly one target, so nothing about the existing single-audit flow changes.
 */

// Only used to preview the number the backend will mint; the backend remains authoritative.
const PREFIX_BY_STANDARD_KEYWORD = [
  ['27001', 'ISMS'],
  ['9001', 'QMS'],
  ['14001', 'EMS'],
  ['45001', 'OHS'],
  ['22301', 'BCMS'],
  ['37001', 'ABMS'],
  ['23257', 'BDLT'],
  ['27701', 'PIMS'],
  ['ISCC', 'ISCC'],
];

const toId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toTarget = (member) => {
  const projectServiceStandardId = toId(member?.projectServiceStandardId);
  if (projectServiceStandardId === null) return null;
  return {
    projectServiceStandardId,
    standardId: toId(member?.standardId),
    standardName: member?.standardName || '',
  };
};

/**
 * @returns {Array<{projectServiceStandardId:number, standardId:number|null, standardName:string}>}
 *          one entry per certificate this scope will issue, deduplicated by standard.
 */
export const resolveCertificateIssuanceStandards = (scope) => {
  if (!scope) return [];

  const members = Array.isArray(scope.memberStandards) && scope.memberStandards.length > 0
    ? scope.memberStandards
    : (Array.isArray(scope.memberScopes) ? scope.memberScopes : []);

  const source = members.length > 0 ? members : [scope];

  const seen = new Set();
  return source.reduce((targets, member) => {
    const target = toTarget(member);
    if (!target || seen.has(target.projectServiceStandardId)) return targets;
    seen.add(target.projectServiceStandardId);
    return targets.concat(target);
  }, []);
};

/** True only when an integrated/combined methodology group genuinely issues several certificates. */
export const isPerStandardCertificateScope = (scope) => {
  const methodology = String(scope?.methodologyType || '').trim().toLowerCase();
  return Boolean(scope?.isMethodologyGroup)
    && (methodology === 'integrated' || methodology === 'combined')
    && resolveCertificateIssuanceStandards(scope).length > 1;
};

export const resolveExpectedCertificatePrefix = (standardName) => {
  const upper = String(standardName || '').toUpperCase();
  if (!upper) return '';
  const match = PREFIX_BY_STANDARD_KEYWORD.find(([keyword]) => upper.includes(keyword));
  return match ? match[1] : '';
};

/** Adds the certificate-number prefix each standard is expected to get, for display only. */
export const describeCertificateIssuanceTargets = (scope) =>
  resolveCertificateIssuanceStandards(scope).map((target) => ({
    ...target,
    expectedPrefix: resolveExpectedCertificatePrefix(target.standardName),
  }));

/**
 * FTRD-02 keeps client/audit data shared while certificate-specific fields are kept per target.
 * This produces the exact payload shape required for one preview per standard and batch issue.
 */
export const buildFtrd02StandardForms = (
  scope,
  sharedForm = {},
  overridesByStandardId = {},
  formsByStandardId = {},
) => describeCertificateIssuanceTargets(scope).map((target) => ({
  // The shared half seeds a standard nobody has opened yet; its own saved snapshot, once it
  // exists, is authoritative for every field it holds.
  ...sharedForm,
  ...(overridesByStandardId[target.projectServiceStandardId] || {}),
  ...(formsByStandardId[target.projectServiceStandardId] || {}),
  projectServiceStandardId: target.projectServiceStandardId,
  standardId: target.standardId,
  standardName: target.standardName,
  applicableStandards: [target.standardName],
}));

/**
 * FTRD-02 in a grouped scope is still ONE saved document, because the audit itself is one
 * audit: client data, audit dates, sites and auditors are shared by every standard. Only the
 * certificate-describing fields differ per standard, so they are stored side by side under
 * `standardOverrides` (keyed by projectServiceStandardId) inside that single row. Saving from
 * one standard's tab therefore never overwrites what the other tabs hold.
 */
export const FTRD_02_DOC_CODE = 'FTRD-02 Certificate Review and Production';

export const FTRD_02_STANDARD_OVERRIDES_KEY = 'standardOverrides';

/**
 * Each standard's COMPLETE form, stored beside the curated per-standard fields. The curated
 * split (`standardOverrides`) exists for the certificate renderer, but it is a fixed list of
 * field names — anything the form gains later, or anything the operator fills that is not on
 * that list, would silently not be kept per standard. The snapshot keeps every bound field.
 */
export const FTRD_02_STANDARD_FORMS_KEY = 'standardForms';

const FTRD_02_PER_STANDARD_FIELDS = new Set([
  'projectServiceStandardId', 'standardId', 'standardName', 'applicableStandards',
  'certificateNumber', 'certificate_number', 'certNumber', 'certificateScope',
  'scopeOfCertification', 'scope', 'statementOfApplicability', 'soa',
  'verificationOfScope', 'certificateConditions', 'conditions', 'remarks', 'notes',
]);

/** Splits one standard's complete FTRD-02 form into the shared half and its own half. */
export const splitFtrd02FormByStandard = (form = {}) => Object.entries(form || {})
  .reduce((result, [key, value]) => {
    // The per-standard maps are containers, never form fields: they must not end up nested.
    if (key === FTRD_02_STANDARD_OVERRIDES_KEY || key === FTRD_02_STANDARD_FORMS_KEY) return result;
    if (FTRD_02_PER_STANDARD_FIELDS.has(key)) {
      return { ...result, overrides: { ...result.overrides, [key]: value } };
    }
    return { ...result, shared: { ...result.shared, [key]: value } };
  }, { shared: {}, overrides: {} });

/** Reads the per-standard map back out of a saved FTRD-02 row, ignoring malformed data. */
export const readFtrd02StandardOverrides = (savedForm) => {
  const stored = savedForm?.[FTRD_02_STANDARD_OVERRIDES_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  return Object.entries(stored).reduce((result, [standardId, overrides]) => (
    overrides && typeof overrides === 'object' && !Array.isArray(overrides)
      ? { ...result, [standardId]: overrides }
      : result
  ), {});
};

/** The saved row without its per-standard maps — i.e. the fields every standard shares. */
export const readFtrd02SharedForm = (savedForm) => {
  const {
    [FTRD_02_STANDARD_OVERRIDES_KEY]: _overrides,
    [FTRD_02_STANDARD_FORMS_KEY]: _forms,
    ...shared
  } = savedForm || {};
  return shared;
};

/** Reads the complete-form snapshots back out of a saved FTRD-02 row. */
export const readFtrd02StandardForms = (savedForm) => {
  const stored = savedForm?.[FTRD_02_STANDARD_FORMS_KEY];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  return Object.entries(stored).reduce((result, [standardId, form]) => (
    form && typeof form === 'object' && !Array.isArray(form)
      ? { ...result, [standardId]: form }
      : result
  ), {});
};

/**
 * Builds exactly what gets written to the single FTRD-02 row when the active standard's tab
 * is saved: its shared fields, its own certificate fields, and every other standard's
 * certificate fields carried over untouched.
 */
export const buildFtrd02PersistedForm = (
  activeForm,
  activeStandardId,
  overridesByStandardId = {},
  formsByStandardId = {},
) => {
  const { shared, overrides } = splitFtrd02FormByStandard(activeForm);
  const carriedOverrides = readFtrd02StandardOverrides({
    [FTRD_02_STANDARD_OVERRIDES_KEY]: overridesByStandardId,
  });
  const carriedForms = readFtrd02StandardForms({
    [FTRD_02_STANDARD_FORMS_KEY]: formsByStandardId,
  });
  const activeKey = activeStandardId === null || activeStandardId === undefined
    ? '' : String(activeStandardId);
  // The complete snapshot is what makes the save lossless: every field the form binds is kept
  // for this standard, whether or not it is one of the curated certificate fields.
  const activeSnapshot = { ...shared, ...overrides };
  return {
    ...shared,
    [FTRD_02_STANDARD_OVERRIDES_KEY]: activeKey
      ? { ...carriedOverrides, [activeKey]: overrides }
      : carriedOverrides,
    [FTRD_02_STANDARD_FORMS_KEY]: activeKey
      ? { ...carriedForms, [activeKey]: activeSnapshot }
      : carriedForms,
  };
};
